/**
 * ExtractionDetail — P22 D2 Review Queue page.
 *
 * Renders an ExtractionJob's extracted items and lets the operator:
 *  - Edit commercial fields (title, VAT category, base price, MOQ,
 *    order increment) via debounced PATCH on each item. SKU is no longer
 *    operator-set — it is sequence-assigned by the DB at promote time.
 *  - Approve / Reject individual items.
 *  - Promote all approved items to the product catalog via POST .../promote.
 *
 * Polling: while job.status is "queued" or "extracting" we re-fetch every
 * 2 s. The interval is cleared on unmount and when the job reaches a
 * terminal status (ready | failed | completed).
 *
 * VAT categories: fetched via useVatCategories() at this level and threaded
 * to ExtractedItemCard (single fetch, not N+1).
 *
 * Price convention: minor units (pence), matching VariantEditModal's
 * base_price_minor field.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  StatusPill,
  type Tone,
  useToast as useUIToast,
} from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";
import { useVatCategories } from "../lib/vat-categories.js";
import type {
  ExtractedItemDTO,
  ExtractedItemEdits,
  ExtractedItemStatus,
  ExtractionJobDTO,
  ExtractionJobStatus,
  PromoteResultDTO,
  VatCategoryDTO,
} from "@wmart/shared-types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLLING_INTERVAL_MS = 2000;
const DEBOUNCE_MS = 400;

/** Job statuses that require polling. */
const POLLING_STATUSES: ExtractionJobStatus[] = ["queued", "extracting"];

const STATUS_TONE: Record<ExtractionJobStatus, Tone> = {
  queued: "neutral",
  extracting: "info",
  ready: "success",
  failed: "danger",
  completed: "olive",
};

const ITEM_STATUS_TONE: Record<ExtractedItemStatus, Tone> = {
  pending: "neutral",
  approved: "success",
  rejected: "danger",
  promoted: "olive",
};

// ---------------------------------------------------------------------------
// ExtractedItemCard sub-component
// ---------------------------------------------------------------------------

interface ExtractedItemCardProps {
  jobId: string;
  item: ExtractedItemDTO;
  vatCategories: VatCategoryDTO[];
  onUpdated: (updated: ExtractedItemDTO) => void;
}

function ExtractedItemCard({ jobId, item, vatCategories, onUpdated }: ExtractedItemCardProps) {
  const { notify } = useToast();

  // Local draft state for editable fields — initialized from item.edited,
  // falling back to extracted where sensible.
  const [draftTitle, setDraftTitle] = useState(
    item.edited.title ?? item.extracted.title ?? "",
  );
  const [draftVariantLabel, setDraftVariantLabel] = useState(
    item.edited.variant_label ?? item.extracted.variant_label ?? "",
  );
  // Operator-pasted product image URL. Maps to the item's `image_url` column
  // (top-level on the DTO, NOT inside edited) — image/PDF extractions don't
  // yield a hosted URL, so the operator supplies one for promote to attach.
  const [draftImageUrl, setDraftImageUrl] = useState(
    item.image_url ?? item.extracted.image_url ?? "",
  );
  const [draftVat, setDraftVat] = useState(
    item.edited.vat_category_code ?? "food_zero",
  );
  const [draftPrice, setDraftPrice] = useState(
    item.edited.base_price_minor != null
      ? String(item.edited.base_price_minor)
      : item.extracted.suggested_price_minor != null
        ? String(item.extracted.suggested_price_minor)
        : "",
  );
  const [draftMoq, setDraftMoq] = useState(
    item.edited.min_order_quantity != null
      ? String(item.edited.min_order_quantity)
      : "1",
  );
  const [draftIncrement, setDraftIncrement] = useState(
    item.edited.order_increment != null
      ? String(item.edited.order_increment)
      : "1",
  );

  const [patching, setPatching] = useState(false);

  // Debounce timer ref — cleared on each field change before setting a new one.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Separate timer for the image-URL field (a top-level patch, not `edited`),
  // so it doesn't race the editedJson debounce above.
  const imageDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const baseUrl = `/v1/admin/catalog/extractions/${jobId}/items/${item.id}`;

  const isEditable =
    item.status === "pending" || item.status === "approved";

  // ---- debounced patch helper ----
  const patchEdited = useCallback(
    (partial: ExtractedItemEdits) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        setPatching(true);
        try {
          const updated = await api.patch<ExtractedItemDTO>(baseUrl, {
            edited: partial,
          });
          onUpdated(updated);
        } catch (err) {
          if (err instanceof ApiError && err.problem) {
            notify("error", err.problem.title ?? "Save failed");
          } else {
            notify("error", "Save failed");
          }
        } finally {
          setPatching(false);
        }
      }, DEBOUNCE_MS);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseUrl],
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (imageDebounceRef.current) clearTimeout(imageDebounceRef.current);
    };
  }, []);

  // ---- image-URL change → debounced top-level PATCH { image_url } ----
  function handleImageUrlChange(v: string) {
    setDraftImageUrl(v);
    if (imageDebounceRef.current) clearTimeout(imageDebounceRef.current);
    imageDebounceRef.current = setTimeout(async () => {
      setPatching(true);
      try {
        const updated = await api.patch<ExtractedItemDTO>(baseUrl, {
          image_url: v,
        });
        onUpdated(updated);
      } catch (err) {
        if (err instanceof ApiError && err.problem) {
          notify("error", err.problem.title ?? "Save failed");
        } else {
          notify("error", "Save failed");
        }
      } finally {
        setPatching(false);
      }
    }, DEBOUNCE_MS);
  }

  // ---- build edits object (omits keys whose values are empty/undefined) ----
  // Takes the full draft snapshot; each handler passes current drafts with the
  // one changed field overridden (setState is async, so we can't read it back).
  function buildEdits(d: {
    title: string;
    variantLabel: string;
    vatCategory: string;
    price: string;
    moq: string;
    increment: string;
  }): ExtractedItemEdits {
    const edits: ExtractedItemEdits = {};
    if (d.title) edits.title = d.title;
    if (d.variantLabel) edits.variant_label = d.variantLabel;
    if (d.vatCategory) edits.vat_category_code = d.vatCategory;
    if (d.price !== "") edits.base_price_minor = Number(d.price);
    if (d.moq !== "") edits.min_order_quantity = Number(d.moq);
    if (d.increment !== "") edits.order_increment = Number(d.increment);
    return edits;
  }

  // Current draft snapshot, used as the base for each handler's override.
  function draftSnapshot() {
    return {
      title: draftTitle,
      variantLabel: draftVariantLabel,
      vatCategory: draftVat,
      price: draftPrice,
      moq: draftMoq,
      increment: draftIncrement,
    };
  }

  // ---- field change handlers ----
  function handleTitleChange(v: string) {
    setDraftTitle(v);
    patchEdited(buildEdits({ ...draftSnapshot(), title: v }));
  }

  function handleVariantLabelChange(v: string) {
    setDraftVariantLabel(v);
    patchEdited(buildEdits({ ...draftSnapshot(), variantLabel: v }));
  }

  function handleVatChange(v: string) {
    setDraftVat(v);
    patchEdited(buildEdits({ ...draftSnapshot(), vatCategory: v }));
  }

  function handlePriceChange(v: string) {
    setDraftPrice(v);
    patchEdited(buildEdits({ ...draftSnapshot(), price: v }));
  }

  function handleMoqChange(v: string) {
    setDraftMoq(v);
    patchEdited(buildEdits({ ...draftSnapshot(), moq: v }));
  }

  function handleIncrementChange(v: string) {
    setDraftIncrement(v);
    patchEdited(buildEdits({ ...draftSnapshot(), increment: v }));
  }

  // ---- approve / reject ----
  async function patchStatus(status: "approved" | "rejected") {
    setPatching(true);
    try {
      // On approve, flush the FULL visible draft alongside the status flip.
      // The VAT/category selects render a default (e.g. "Food (zero rate)")
      // that is only persisted via their onChange handler — if the operator
      // approves without touching them, those defaults were never saved and
      // promote later fails with "unknown vat_category_code". Cancel any
      // in-flight debounced patch first so this write is authoritative.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (imageDebounceRef.current) {
        clearTimeout(imageDebounceRef.current);
        imageDebounceRef.current = null;
      }
      const body =
        status === "approved"
          ? {
              status,
              edited: buildEdits(draftSnapshot()),
              image_url: draftImageUrl,
            }
          : { status };
      const updated = await api.patch<ExtractedItemDTO>(baseUrl, body);
      onUpdated(updated);
    } catch (err) {
      if (err instanceof ApiError && err.problem) {
        notify("error", err.problem.title ?? "Action failed");
      } else {
        notify("error", "Action failed");
      }
    } finally {
      setPatching(false);
    }
  }

  const disabled = patching || !isEditable;

  return (
    <Card className="p-5 flex flex-col gap-4">
      {/* Header row: thumbnail + extracted reference + status */}
      <div className="flex gap-4 items-start">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.extracted.title ?? "product"}
            className="h-20 w-20 flex-shrink-0 rounded-sm border border-ink-200 object-cover"
          />
        ) : (
          <div className="h-20 w-20 flex-shrink-0 rounded-sm border border-ink-200 bg-ink-50 flex items-center justify-center text-caption text-ink-400">
            No image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <StatusPill tone={ITEM_STATUS_TONE[item.status]}>
              {item.status}
            </StatusPill>
            {item.promoted_product_id ? (
              <span className="text-caption text-ink-500">
                → product {item.promoted_product_id}
              </span>
            ) : null}
          </div>
          <p className="font-medium text-body-sm text-ink-900 truncate">
            {item.extracted.title ?? "(no title)"}
          </p>
          {item.extracted.description ? (
            <p className="text-caption text-ink-500 line-clamp-2">
              {item.extracted.description}
            </p>
          ) : null}
          {item.extracted.pack_size ? (
            <p className="text-caption text-ink-500">
              Pack: {item.extracted.pack_size}
            </p>
          ) : null}
          {item.extracted.suggested_price_minor != null ? (
            <p className="text-caption text-ink-500">
              Suggested: {item.extracted.suggested_price_minor}p
            </p>
          ) : null}
        </div>
      </div>

      {/* Editable fields */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Title"
          value={draftTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          disabled={disabled}
        />
        <Input
          label="Variant label"
          placeholder="e.g. With Date, Vegan"
          value={draftVariantLabel}
          onChange={(e) => handleVariantLabelChange(e.target.value)}
          disabled={disabled}
        />
        <div className="flex flex-col gap-1">
          <Input
            label="Image URL"
            placeholder="https://… (paste a product image)"
            value={draftImageUrl}
            onChange={(e) => handleImageUrlChange(e.target.value)}
            disabled={disabled}
          />
          {draftImageUrl ? (
            <img
              src={draftImageUrl}
              alt="preview"
              className="mt-1 h-16 w-16 rounded-sm border border-ink-200 object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
            />
          ) : null}
        </div>
        <Select
          label="VAT category"
          value={draftVat}
          onChange={(e) => handleVatChange(e.target.value)}
          disabled={disabled}
        >
          {vatCategories.map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </Select>
        <Input
          type="number"
          step={1}
          min={0}
          label="Base price (minor units)"
          placeholder="e.g. 150 for £1.50"
          helper="In pence"
          value={draftPrice}
          onChange={(e) => handlePriceChange(e.target.value)}
          disabled={disabled}
        />
        <Input
          type="number"
          step={1}
          min={1}
          label="Min order quantity"
          value={draftMoq}
          onChange={(e) => handleMoqChange(e.target.value)}
          disabled={disabled}
        />
        <Input
          type="number"
          step={1}
          min={1}
          label="Order increment"
          value={draftIncrement}
          onChange={(e) => handleIncrementChange(e.target.value)}
          disabled={disabled}
        />
      </div>

      {/* Approve / Reject actions */}
      {isEditable ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={item.status === "approved" ? "primary" : "secondary"}
            onClick={() => void patchStatus("approved")}
            disabled={patching}
            loading={patching}
            aria-label={`Approve ${item.extracted.title ?? item.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void patchStatus("rejected")}
            disabled={patching}
            loading={patching}
            className="text-danger"
            aria-label={`Reject ${item.extracted.title ?? item.id}`}
          >
            Reject
          </Button>
        </div>
      ) : null}

    </Card>
  );
}

// ---------------------------------------------------------------------------
// ExtractionDetail main component
// ---------------------------------------------------------------------------

export function ExtractionDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const { push } = useUIToast();
  const { data: vatCategories } = useVatCategories();

  const [job, setJob] = useState<ExtractionJobDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJob = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<ExtractionJobDTO>(
        `/v1/admin/catalog/extractions/${id}`,
      );
      setJob(data);
      // NB: polling start/stop is driven solely by the status effect below,
      // keyed on `job.status`. fetchJob never touches intervalRef so a
      // terminal job opened directly never schedules a phantom poll.
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load extraction job");
    } finally {
      setLoading(false);
    }
  }, [id, notify, notifyProblem]);

  // Mount: initial fetch only. Polling is NOT started here — see the
  // status-driven effect below, which is the single source of truth for
  // starting/stopping the interval. This avoids a phantom GET firing at
  // t+2s for a job that resolved terminal on the first fetch.
  useEffect(() => {
    void fetchJob();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Single source of truth for polling: start the interval when the job is
  // in a polling status (queued/extracting) and no interval is registered;
  // clear it otherwise (terminal status, or job not yet loaded). The
  // unmount cleanup also clears it.
  useEffect(() => {
    const polling = job != null && POLLING_STATUSES.includes(job.status);
    if (polling) {
      if (intervalRef.current === null) {
        intervalRef.current = setInterval(() => {
          void fetchJob();
        }, POLLING_INTERVAL_MS);
      }
    } else if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.status]);

  function handleItemUpdated(updated: ExtractedItemDTO) {
    setJob((prev) => {
      if (!prev) return prev;
      const nextItems = prev.items?.map((it) => {
        if (it.id !== updated.id) return it;
        // Race guard: a debounced PATCH that resolves AFTER a successful
        // promote must not downgrade a freshly-promoted item back to its
        // pre-promote status (which would clear promoted_product_id).
        return it.status === "promoted" && updated.status !== "promoted"
          ? it
          : updated;
      });
      // Use exactOptionalPropertyTypes-safe spread: only set items key if defined
      if (nextItems === undefined) {
        const { items: _items, ...rest } = prev;
        void _items;
        return rest as ExtractionJobDTO;
      }
      return { ...prev, items: nextItems };
    });
  }

  async function handlePromote() {
    if (!id) return;
    try {
      const result = await api.post<PromoteResultDTO>(
        `/v1/admin/catalog/extractions/${id}/promote`,
        {},
      );
      notify("success", `Promoted ${result.promoted.length} item(s)`);
      for (const f of result.failed) {
        notify("error", `Item ${f.item_id}: ${f.reason}`);
      }
      for (const w of result.image_warnings) {
        push({
          tone: "warning",
          title: "Image warning",
          body: `Item ${w.item_id}: ${w.reason}`,
        });
      }
      // Await the re-fetch so the UI settles on server state — prevents a
      // still-in-flight debounced PATCH from racing the promoted statuses.
      await fetchJob();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Promote failed");
    }
  }

  // ---- Loading state ----
  if (loading && !job) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-body-sm text-ink-500">Loading…</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-body-sm text-danger">Job not found.</p>
      </div>
    );
  }

  // ---- Polling / in-progress state ----
  const isInProgress = POLLING_STATUSES.includes(job.status);
  const approvedCount = job.items?.filter((i) => i.status === "approved").length ?? 0;

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      {/* Page header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-display-sm text-ink-900">
            Extraction review
          </h1>
          <div className="mt-1 flex items-center gap-2">
            <StatusPill tone={STATUS_TONE[job.status]}>{job.status}</StatusPill>
            <span className="text-caption text-ink-500 font-mono">
              {job.source_type} ·{" "}
              {job.source_ref.length > 60
                ? `${job.source_ref.slice(0, 59)}…`
                : job.source_ref}
            </span>
          </div>
        </div>
        {(job.status === "ready" || job.status === "completed") &&
        approvedCount > 0 ? (
          <Button onClick={() => void handlePromote()}>
            Promote approved ({approvedCount})
          </Button>
        ) : null}
      </div>

      {/* In-progress spinner */}
      {isInProgress ? (
        <Card className="p-6">
          <p
            role="status"
            aria-live="polite"
            className="text-body-sm text-ink-500 animate-pulse"
          >
            Extracting… This may take a minute. The page will update
            automatically.
          </p>
        </Card>
      ) : null}

      {/* Error state */}
      {job.status === "failed" ? (
        <Card className="p-6">
          <p className="text-body-sm font-medium text-danger">
            Extraction failed
          </p>
          {job.error_text ? (
            <p className="mt-1 text-body-sm text-ink-700">{job.error_text}</p>
          ) : null}
        </Card>
      ) : null}

      {/* Items list */}
      {!isInProgress && job.status !== "failed" ? (
        job.items && job.items.length > 0 ? (
          <div className="flex flex-col gap-4">
            {job.items.map((item) => (
              <ExtractedItemCard
                key={item.id}
                jobId={job.id}
                item={item}
                vatCategories={vatCategories}
                onUpdated={handleItemUpdated}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="fileText"
            title="No products were extracted"
            body="The extraction completed but found no product items."
          />
        )
      ) : null}
    </div>
  );
}
